import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import { 
  ArrowLeft, Shield, FileSearch, FlaskConical, Users, 
  CheckCircle2, AlertTriangle, ExternalLink, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import GlobalFooter from '../components/GlobalFooter';

export default function GuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const safetyChecks = [
    {
      number: '01',
      icon: FileSearch,
      title: 'Third-Party Testing (USP / NSF / ConsumerLab)',
      description: 'Look for seals from independent testing organizations that verify the product contains what it claims and is free from contaminants.',
      tips: [
        'USP Verified mark ensures quality, purity, and potency',
        'NSF Certified for Sport is essential for athletes',
        'ConsumerLab.com tests products independently',
        'Avoid products with no third-party verification'
      ],
      color: 'bg-blue-50 border-blue-200 text-blue-800'
    },
    {
      number: '02',
      icon: FlaskConical,
      title: 'Form & Bioavailability Matter',
      description: 'Not all supplement forms are created equal. The form determines how well your body can absorb and use the nutrient.',
      tips: [
        'Magnesium glycinate absorbs better than magnesium oxide',
        'Methylfolate is superior to folic acid for many people',
        'Vitamin D3 is more effective than D2',
        'Curcumin needs piperine or liposomal delivery for absorption'
      ],
      color: 'bg-green-50 border-green-200 text-green-800'
    },
    {
      number: '03',
      icon: Shield,
      title: 'Check for Red Flags',
      description: 'Be wary of products that make outrageous claims, hide behind proprietary blends, or lack transparency.',
      tips: [
        'Avoid "proprietary blends" that hide ingredient amounts',
        'Be skeptical of "miracle cure" or "breakthrough" claims',
        'Check for artificial colors, fillers, and unnecessary additives',
        'Research the manufacturer\'s reputation and recall history'
      ],
      color: 'bg-amber-50 border-amber-200 text-amber-800'
    },
    {
      number: '04',
      icon: Users,
      title: 'Consult Before You Start',
      description: 'Always discuss new supplements with your healthcare provider, especially if you take medications or have health conditions.',
      tips: [
        'Some supplements interact with prescription medications',
        'Certain conditions require adjusted dosages or avoidance',
        'Your doctor can order tests to identify actual deficiencies',
        'A pharmacist can check for drug-supplement interactions'
      ],
      color: 'bg-purple-50 border-purple-200 text-purple-800'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-16 sm:w-20" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <Award className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
            The Vitala Buyer's Safety Guide
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Before you buy any supplement—whether from our recommendations or elsewhere—use this 4-point checklist 
            to protect yourself from low-quality or potentially harmful products.
          </p>
        </motion.div>

        {/* Warning Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8"
        >
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-2">Why This Matters</h3>
              <p className="text-amber-800 text-sm leading-relaxed">
                The supplement industry is largely unregulated. Unlike prescription drugs, supplements don't require 
                FDA approval before hitting the shelves. This means quality varies wildly—some products contain less 
                (or more) than labeled, while others may be contaminated with heavy metals, pesticides, or undisclosed 
                ingredients. Your safety depends on being an informed consumer.
              </p>
            </div>
          </div>
        </motion.div>

        {/* The 4-Point Checklist */}
        <div className="space-y-6">
          {safetyChecks.map((check, index) => (
            <motion.div
              key={check.number}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.1 }}
              className={`rounded-2xl p-6 border ${check.color}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <check.icon className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl font-bold opacity-50">{check.number}</span>
                    <h3 className="text-lg font-semibold">{check.title}</h3>
                  </div>
                  <p className="mb-4 opacity-90">{check.description}</p>
                  <ul className="space-y-2">
                    {check.tips.map((tip, tipIndex) => (
                      <li key={tipIndex} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-70" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trusted Resources Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 bg-white rounded-2xl p-6 border border-border"
        >
          <h3 className="text-xl font-semibold text-[#1A1A1A] mb-4">Trusted Resources</h3>
          <p className="text-muted-foreground text-sm mb-4">
            These independent organizations test and verify supplement quality:
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            <a 
              href="https://www.usp.org/verification-services" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-[#1A1A1A]"
            >
              <ExternalLink className="w-4 h-4 text-primary" />
              USP Verified
            </a>
            <a 
              href="https://www.nsfsport.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-[#1A1A1A]"
            >
              <ExternalLink className="w-4 h-4 text-primary" />
              NSF Certified
            </a>
            <a 
              href="https://www.consumerlab.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-[#1A1A1A]"
            >
              <ExternalLink className="w-4 h-4 text-primary" />
              ConsumerLab
            </a>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">
              Ready for Personalized Recommendations?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Our AI-powered assessment considers your unique health profile and provides 
              evidence-based supplement suggestions with safety checks built in.
            </p>
            <Button 
              asChild
              className="btn-primary"
            >
              <Link to="/quiz" data-testid="guide-start-quiz-btn">
                Take Free Assessment
              </Link>
            </Button>
          </div>
        </motion.div>
      </main>

      <GlobalFooter />
    </div>
  );
}
